import React, { useMemo, useState } from 'react';

// ─── Regrouper les concessions — trois mailles, une seule mécanique ──────────
// Deux entités juridiques distinctes peuvent ne former qu'un seul SITE (Soguava
// et Sorec en Guadeloupe), et la métropole se lit aussi par PLAQUE commerciale.
// Les deux clés vivent dans le référentiel `bi_societes` (`concession_master`,
// `concession_plaque`), en libellé libre.
//
// La maille est un RÉGLAGE D'AFFICHAGE : elle se choisit dans l'en-tête de
// carte (CLAUDE.md §2), pas dans le panneau de filtres, et elle ne change aucun
// chiffre — seulement la façon de les empiler.
//
// ⚠ On ne somme QUE des mesures additives ; les taux se recalculent ensuite à
// partir des sommes. Moyenner des taux de marge donnerait un chiffre faux dès
// que deux concessions n'ont pas le même volume.

export const NIVEAUX = [
  { c: 'concession', lib: 'Concessions' },
  { c: 'master', lib: 'Concessions groupées' },
  { c: 'plaque', lib: 'Plaque' },
];

// Les clés de la hiérarchie, du plus large au plus fin, selon la maille.
const AXES = { concession: [], master: ['master'], plaque: ['plaque', 'master'] };

// Maille par défaut : « Concessions groupées » (décision du 30/09/2026, les
// deux branches) — la lecture de pilotage se fait au niveau du site. Une
// branche sans regroupement renseigné retombe d'elle-même sur les concessions
// (construireArbre : une société sans valeur descend d'un cran).
export function useRegroupement() {
  const [niveau, setNiveau] = useState('master');
  const [ouverts, setOuverts] = useState(() => new Set());
  const basculer = (cle) => setOuverts((s) => {
    const n = new Set(s); n.has(cle) ? n.delete(cle) : n.add(cle); return n;
  });
  // `ouvrirTout` : « Tout déplier » des écrans qui l'offrent (OR en cours).
  const ouvrirTout = (cles) => setOuverts(new Set(cles));
  return { niveau, setNiveau, ouverts, basculer, ouvrirTout };
}

// Sélecteur `st-seg`, dans l'en-tête de carte. Un axe sans aucune valeur
// renseignée est désactivé plutôt que de proposer une vue vide : aux Antilles
// la plaque n'est pas saisie, et un bouton qui ne produit rien se lit comme
// une panne.
export function SelecteurMaille({ niveau, setNiveau, dispo }) {
  // Maille demandée mais non renseignée sur le périmètre : l'écran montre les
  // concessions, le sélecteur le dit.
  const effectif = niveau !== 'concession' && !dispo[niveau] ? 'concession' : niveau;
  return (
    <div className="st-seg">
      {NIVEAUX.map((n) => {
        const off = n.c !== 'concession' && !dispo[n.c];
        return (
          <button key={n.c} type="button" disabled={off}
            className={effectif === n.c ? 'on' : ''}
            title={off ? `Aucune valeur « ${n.lib} » dans le référentiel des sociétés` : ''}
            onClick={() => setNiveau(n.c)}>{n.lib}</button>
        );
      })}
    </div>
  );
}

// Quels axes sont réellement renseignés sur le périmètre courant.
export function axesDisponibles(feuilles) {
  return {
    master: feuilles.some((f) => f.master),
    plaque: feuilles.some((f) => f.plaque),
  };
}

// Construit les lignes à afficher, aplaties, chacune portant son niveau
// d'indentation. `sommer(feuilles)` rend les mesures d'un regroupement — c'est
// la MÊME fonction que pour une concession seule, sinon les totaux divergent.
//
// Deux règles de bon sens, qui évitent des lignes creuses :
//   • un regroupement à UN seul membre s'efface : on affiche directement la
//     concession, avec son nom. Il n'y a rien à déplier ;
//   • une société sans valeur sur un axe retombe au niveau du dessous plutôt
//     que d'atterrir dans un fourre-tout « sans plaque » — aux Antilles, où la
//     plaque n'est pas saisie, la vue reste ainsi lisible.
export function construireArbre(feuilles, niveau, sommer, ouverts, valeurDe) {
  const axes = AXES[niveau] || [];
  const tri = (l) => (valeurDe ? [...l].sort((a, b) => (valeurDe(b) || 0) - (valeurDe(a) || 0)) : l);
  if (axes.length === 0) {
    return tri(feuilles).map((f) => ({ ...f, niveau: 0, feuille: true, cle: f.code || f.libelle }));
  }

  const noeud = (liste, profondeur, prefixe) => {
    const axe = axes[profondeur];
    if (axe === undefined) {
      return liste.map((f) => ({ ...f, niveau: profondeur, feuille: true, cle: `${prefixe}/${f.code || f.libelle}` }));
    }
    // Les sociétés sans valeur sur cet axe descendent d'un cran.
    const avec = new Map(); const sans = [];
    liste.forEach((f) => {
      const v = f[axe];
      if (!v) { sans.push(f); return; }
      if (!avec.has(v)) avec.set(v, []);
      avec.get(v).push(f);
    });

    const lignes = [];
    for (const [val, membres] of avec) {
      const cle = `${prefixe}/${val}`;
      const enfants = noeud(membres, profondeur + 1, cle);
      // Un groupe d'un seul membre ne s'affiche pas comme groupe.
      if (enfants.length === 1 && enfants[0].feuille) {
        lignes.push({ ...enfants[0], niveau: profondeur, cle });
        continue;
      }
      lignes.push({
        libelle: val, cle, niveau: profondeur, feuille: false,
        nbEnfants: membres.length, membres: membres.map((m) => m.code).filter(Boolean),
        ...sommer(membres),
        enfants,
      });
    }
    // Les orphelins de l'axe, traités au niveau suivant, puis remontés ici.
    lignes.push(...noeud(sans, profondeur + 1, prefixe).map((l) => ({ ...l, niveau: profondeur })));
    return tri(lignes);
  };

  // Aplatissement : on ne descend que dans ce qui est ouvert.
  const plat = [];
  const pousser = (lignes) => lignes.forEach((l) => {
    plat.push(l);
    if (!l.feuille && ouverts.has(l.cle)) pousser(l.enfants);
  });
  pousser(noeud(feuilles, 0, ''));
  return plat;
}

export function useAxes(feuilles) {
  return useMemo(() => axesDisponibles(feuilles), [feuilles]);
}

// ─── Les sections de regroupement du panneau de filtres ─────────────────────
// Elles ne portent aucun état propre : une valeur est cochée quand tous ses
// membres le sont, et la cocher revient à cocher ses membres. Le filtre reste
// unique — la liste de sociétés dans l'URL (CLAUDE.md §4 bis, règle 1).
export function sectionsGroupes(feuilles) {
  const par = (axe) => {
    const m = new Map();
    feuilles.forEach((f) => {
      const v = f[axe];
      if (!v || !f.code) return;
      if (!m.has(v)) m.set(v, []);
      m.get(v).push(f.code);
    });
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b, 'fr'))
      .map(([nom, membres]) => ({ code: `${axe}:${nom}`, nom, membres }));
  };
  return [
    { titre: 'Plaque', valeurs: par('plaque') },
    { titre: 'Concessions groupées', valeurs: par('master') },
  ];
}

// Cocher/décocher un regroupement = cocher/décocher ses membres, puis
// normaliser : tout sélectionné ⇒ pas de filtre, rien ⇒ le marqueur d'absence.
export function basculerGroupe(groupe, selection, societesDispo) {
  const set = new Set(selection);
  const tous = groupe.membres.every((m) => set.has(m));
  groupe.membres.forEach((m) => (tous ? set.delete(m) : set.add(m)));
  const l = [...set];
  if (l.length === 0) return '__aucune__';
  if (l.length === societesDispo.length) return '';
  return l.join(',');
}

// Le libellé du périmètre courant, pour le bandeau : « GUADELOUPE » plutôt que
// « 2 sociétés » quand la sélection coïncide exactement avec un regroupement.
export function nomDuPerimetre(feuilles, selection) {
  if (!selection || selection.length === 0) return null;
  if (selection.length === 1) {
    const f = feuilles.find((x) => x.code === selection[0]);
    return f ? (f.libelle || f.societe) : null;
  }
  for (const axe of ['plaque', 'master']) {
    const vals = new Set(feuilles.filter((f) => selection.includes(f.code)).map((f) => f[axe]));
    if (vals.size !== 1 || !vals.has([...vals][0]) || ![...vals][0]) continue;
    const val = [...vals][0];
    const membres = feuilles.filter((f) => f[axe] === val).map((f) => f.code);
    if (membres.length === selection.length && membres.every((m) => selection.includes(m))) return val;
  }
  return null;
}
